import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import Button from 'components/Button';
import InputField from 'components/InputField';
import { getDynamicTableRows, updateDynamicTableRow } from 'api/sections';
import { handleErrorResponse } from 'utils/general';
import { useAuth } from 'contexts/auth';
import { CircularProgress } from '@mui/material';

const EditInputsFormSection = ({ update_success_message, section, ...props }) => {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const METADATA_COLLECTION_DEFINITION_ID = '0fb16a96-852a-46a6-a039-14841371124f';
  const METADATA_FINGERPRINT_MASTER_LIST_ATTRIBUTE_ID = '0196a524-a444-7ef8-a634-367761f9480f';
  const FINGERPRINT_MASTER_COLLECTION_DEFINITION_ID = 'ae2a2f58-99d8-4744-bab5-1249c7d7e293';
  const FORM_FINGERPRINTS_ATTRIBUTE_ID = 'f19c1337-0a41-4720-9a16-8a74ede06ee6';
  const [fetchFingerprintMasters, setFetchFingerprintMasters] = useState(false);
  const [fingerprintMasterOptions, setFingerprintMasterOptions] = useState([]);

  const onClickCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onSubmit = useCallback((values) => {
    const { dyn_t, section_definition } = section;
    const { collection_definition } = section_definition;
    if (values[FORM_FINGERPRINTS_ATTRIBUTE_ID]) {
      // eslint-disable-next-line no-param-reassign
      values[FORM_FINGERPRINTS_ATTRIBUTE_ID] = JSON.stringify(values[FORM_FINGERPRINTS_ATTRIBUTE_ID]);
    }
    const updateBody = {
      json_short_data: JSON.stringify(values),
    };
    return updateDynamicTableRow(collection_definition?.collection_definition_id, dyn_t, updateBody)
      .then(({ data }) => {
        alert(update_success_message);
        navigate(-1);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [section, update_success_message, setAuth, navigate]);

  const formAttributes = useMemo(() => {
    const { section_definition } = section;
    const { collection_definition } = section_definition;
    const { attributes } = collection_definition;
    return (attributes || []).filter(({ name }) => (name || '').match('^value_([0-9]+)$'));
  }, [section]);

  const initialValues = useMemo(() => {
    return formAttributes.reduce((values, attribute) => {
      const newValues = { ...values };
      const attrNum = attribute.name.replace('value_', '');
      newValues[attribute.attribute_id] = props[`value_${attrNum}`];
      if (props[`value_${attrNum}.target_collection_definition_id`] === METADATA_COLLECTION_DEFINITION_ID
        && props[`value_${attrNum}.target_attribute_id`] === METADATA_FINGERPRINT_MASTER_LIST_ATTRIBUTE_ID) {
        if (!fetchFingerprintMasters) {
          setFetchFingerprintMasters(true);
        }
      }
      return newValues;
    }, {});
  }, [fetchFingerprintMasters, formAttributes, props]);

  const getColumnWidth = (isSubmitting, dirty, isField) => {
    if (isSubmitting || !dirty) {
      return 12;
    }
    if (isField) {
      return 12;
    }
    return 2;
  };

  const fetchDynamicTableRowDataFromApi = useCallback((dynamicTableId) => {
    if (dynamicTableId) {
      const params = {
        $expand: 'attributes',
        $orderby: 'createddate desc',
      };
      return getDynamicTableRows(dynamicTableId, params)
        .then(({ data }) => {
          const result = data.map((item) => {
            const parsedData = JSON.parse(item.json_short_data);
            return {
              value: item.id,
              label: parsedData['3aa8f6cf-7326-4eeb-b0f8-2d6f713d887e'], // fingerprint master's name
            };
          });
          return result;
        })
        .catch((err) => {
          handleErrorResponse(err, setAuth);
        });
    }
  }, [setAuth]);

  const fetchFingerprintMasterFromApi = useCallback(() => {
    return fetchDynamicTableRowDataFromApi(FINGERPRINT_MASTER_COLLECTION_DEFINITION_ID)
      .then((options) => {
        setFingerprintMasterOptions(options);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
        return null;
      });
  }, [fetchDynamicTableRowDataFromApi, setAuth]);

  useEffect(() => {
    if (fetchFingerprintMasters) {
      fetchFingerprintMasterFromApi();
    }
  }, [fetchFingerprintMasterFromApi, fetchFingerprintMasters]);

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      enableReinitialize
      {...props}
    >
      {({ handleChange, setFieldValue, handleBlur, handleSubmit, errors, values, isSubmitting, dirty }) => {
        return (
          <MKBox component="form" role="form" onSubmit={handleSubmit}>
            {formAttributes.sort(
              (a1, a2) => a1.name.localeCompare(a2.name),
            ).map((attribute) => {
              const attrNum = attribute.name.replace('value_', '');
              if (attribute.attribute_id === FORM_FINGERPRINTS_ATTRIBUTE_ID && values[attribute.attribute_id] && values[attribute.attribute_id].includes('[')) {
                // eslint-disable-next-line no-param-reassign
                values[attribute.attribute_id] = JSON.parse(values[attribute.attribute_id])
              }
              return (
                <Grid container spacing={1} mt="1rem" justifyContent="center" alignItems="center">
                  {!!attribute && (
                  <Grid item xs={getColumnWidth(isSubmitting, dirty, true)}>
                    <InputField
                      name={attribute.attribute_id}
                      label={props[`label_${attrNum}`]}
                      value={values[attribute.attribute_id]}
                      type={props[`input_type_${attrNum}`]}
                      button_text="Upload"
                      max_file_size={props[`max_file_size_${attrNum}`]}
                      accept_file_types={props[`accept_file_type_${attrNum}`]}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      handleBlur={handleBlur}
                      {...(props[`input_type_${attrNum}`] === 'multiple_select'
                          && props[`value_${attrNum}.target_attribute_id`] === METADATA_FINGERPRINT_MASTER_LIST_ATTRIBUTE_ID && {
                        type: 'select',
                        multiple: true,
                        options: fingerprintMasterOptions,
                      })}
                    />
                  </Grid>
                  )}
                </Grid>
              );
            })}
            {errors.form && (
              <Grid container justifyContent="flex-end">
                <Grid item xs={12} md={6} xl={4}>
                  <MKTypography variant="caption" color="error">
                    {errors.form}
                    &nbsp;
                  </MKTypography>
                </Grid>
              </Grid>
            )}
            <MKBox mt="1rem">
              {!(isSubmitting || !dirty) ? (
                <Button
                  type="submit"
                  size="large"
                  variant="gradient"
                  color="info"
                  // fullWidth
                  disabled={isSubmitting || !dirty}
                >
                  {isSubmitting ? (
                    <CircularProgress color="white" size={24} sx={{ position: 'absolute' }} />
                  ) : 'Rename'}
                </Button>
              ) : null }
            </MKBox>
          </MKBox>
        );
      }}
    </Formik>
  );
};

EditInputsFormSection.propTypes = {
  // label_1: PropTypes.string,
  // label_2: PropTypes.string,
  // label_3: PropTypes.string,
  // value_1: PropTypes.string,
  // value_2: PropTypes.string,
  // value_3: PropTypes.string,
  // input_type_1: PropTypes.bool,
  // input_type_2: PropTypes.bool,
  // input_type_3: PropTypes.bool,
  // ...etc
  update_success_message: PropTypes.string,
  section: PropTypes.shape({
    dyn_t: PropTypes.string,
    section_definition: PropTypes.shape({
      collection_definition: PropTypes.shape({
        collection_definition_id: PropTypes.string,
        attributes: PropTypes.array,
      }),
    }),
  }),
};

export default EditInputsFormSection;
